import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { findingsApi, type CopilotFindingDto } from '../../api/findings';
import { sendCopilotMessage } from '../../components/CopilotPanel';

/**
 * Mano užklausos — the user's OWN tickets with status (owner ask 2026-09-10:
 * 'clients see their findings and status'). The workspace-scoped list of
 * radiniai they (or the copilot in their chats) registered, each with its
 * live status: Užregistruota → Eilėje → Taisoma → Pataisyta → Įdiegta
 * (Done relabelled 2026-09-11, finding 14 — the fix shipped). No admin
 * chrome — no priorities, no exports, no worker plane; just "my reports and
 * where they stand".
 *
 * Review 2026-09-11 finding 12: every row carries „Peržiūrėti" opening a
 * full-content modal (description, suggested fix, refs, notes — rendered as
 * markdown), and „Redaguoti" when notes exist, opening the same modal
 * straight in notes-editing mode. The modal also offers „Pridėti pastabą"
 * when notes are empty — otherwise notes could never be created.
 */

/** Status chip: sitting → queued → being fixed → done (+ rejected/failed). */
const STATUS_CHIP: Record<string, string> = {
  Registered: 'bg-gray-100 text-gray-600',
  Queued: 'bg-blue-50 text-blue-700',
  PickedUp: 'bg-amber-100 text-amber-700',
  InProgress: 'bg-amber-100 text-amber-700',
  Fixed: 'bg-green-100 text-green-700',
  Failed: 'bg-red-100 text-red-700',
  Done: 'bg-green-100 text-green-700',
  Rejected: 'bg-gray-100 text-gray-400',
};

const KNOWN_STATUSES = ['Registered', 'Queued', 'PickedUp', 'InProgress', 'Fixed', 'Failed', 'Done', 'Rejected'];

const CATEGORY_CHIP: Record<string, string> = {
  ClientPattern: 'bg-amber-100 text-amber-700',
  ProductBug: 'bg-red-100 text-red-700',
  Enhancement: 'bg-primary-50 text-primary-700',
};

const KNOWN_CATEGORIES = ['ClientPattern', 'ProductBug', 'Enhancement'];

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Server-side notes cap (mirrors the PATCH contract). */
const NOTES_MAX_LENGTH = 4000;

/** Offset-less MySQL DATETIME = UTC (house contract; see FindingsBoard). */
function parseAsUtc(iso: string): Date {
  return new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

function ageLabel(iso: string, t: Translate): string {
  const ms = Date.now() - parseAsUtc(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return t('findings.ageDays', { days });
  return t('findings.ageHours', { hours: Math.max(0, Math.floor(ms / 3_600_000)) });
}

function statusLabel(s: string, t: Translate): string {
  return KNOWN_STATUSES.includes(s) ? t(`findings.status.${s}`) : s;
}

function catLabel(c: string, t: Translate): string {
  return KNOWN_CATEGORIES.includes(c) ? t(`findings.cat.${c}`) : c;
}

/**
 * Escape-first markdown (SECURITY): finding content is AI-composed chat
 * text, so EVERYTHING is HTML-escaped BEFORE any transform runs — only our
 * own generated tags ever reach the DOM. Mirrors CopilotPanel.formatMarkdown
 * (fenced ``` blocks, inline code, bold, italic, newlines) plus markdown
 * links: the href pattern admits http/https URLs only, so javascript: and
 * other schemes never become anchors (they stay escaped plain text). The
 * captured URL is already quote-escaped, so it cannot break out of href="".
 */
function renderMarkdownSafe(text: string): string {
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  return esc
    .replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre class="bg-gray-100 rounded p-2 text-xs overflow-x-auto my-1"><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
    .replace(/\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-700 underline">$1</a>')
    .replace(/\n/g, '<br/>');
}

/**
 * The document reference shown in the modal: ONLY the human-readable
 * document number — raw ids never reach the screen (privacy rule).
 * Tolerates both a single ref object and an array of refs; malformed
 * JSON shows nothing.
 */
function refDocumentNumber(refsJson: string): string | null {
  try {
    const refs: unknown = JSON.parse(refsJson);
    const candidates = Array.isArray(refs) ? refs : [refs];
    for (const c of candidates) {
      if (c && typeof c === 'object' && 'documentNumber' in c) {
        const v = (c as { documentNumber?: unknown }).documentNumber;
        if (typeof v === 'string' && v.trim()) return v;
      }
    }
  } catch { /* malformed refs — nothing to show */ }
  return null;
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
      className="w-3.5 h-3.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
      className="w-3.5 h-3.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
  );
}

/**
 * Full-content reader for one finding: title, status/category/age chips,
 * description + suggested fix as (escaped) markdown, the referenced document
 * number, and the user's own notes — viewable and editable in place.
 * Closes on backdrop click, Esc, and the X button.
 */
function FindingModal({
  finding,
  startInEdit,
  onClose,
}: {
  finding: CopilotFindingDto;
  startInEdit: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(startInEdit);
  const [draft, setDraft] = useState(finding.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const notesMutation = useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) => findingsApi.updateNotes(id, notes),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['my-findings'] });
      setEditing(false); // back to view mode — the refetched row carries the new notes
    },
    onError: () => setError(t('myFindings.notesSaveError')),
  });

  const tooLong = draft.length > NOTES_MAX_LENGTH;
  const docNumber = refDocumentNumber(finding.refsJson);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={finding.title}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <h2 className="text-lg font-semibold text-gray-900">{finding.title}</h2>
          <button
            onClick={onClose}
            aria-label={t('myFindings.close')}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-700"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-4 h-4" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_CHIP[finding.status] ?? 'bg-gray-100 text-gray-600'}`}>
            {statusLabel(finding.status, t)}
          </span>
          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${CATEGORY_CHIP[finding.category] ?? 'bg-gray-100 text-gray-600'}`}>
            {catLabel(finding.category, t)}
          </span>
          <span className="text-xs text-gray-400">{ageLabel(finding.createdAtUtc, t)}</span>
        </div>

        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('myFindings.descriptionTitle')}
        </h3>
        <div
          className="mb-4 text-sm text-gray-700"
          dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(finding.description) }}
        />

        {!!finding.suggestedFix && (
          <>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('myFindings.suggestedFixTitle')}
            </h3>
            <div
              className="mb-4 text-sm text-gray-700"
              dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(finding.suggestedFix) }}
            />
          </>
        )}

        {docNumber && (
          <p className="mb-4 text-xs text-gray-500">
            {t('myFindings.documentRef')}:
            <span className="ml-1 font-medium text-gray-700">{docNumber}</span>
          </p>
        )}

        <div className="border-t border-gray-100 pt-3">
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('myFindings.notesTitle')}
          </h3>
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={NOTES_MAX_LENGTH}
                placeholder={t('myFindings.notesPlaceholder')}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm min-h-24 focus:outline-none focus:border-primary-500"
              />
              {tooLong && <p className="text-xs text-red-600">{t('myFindings.notesTooLong')}</p>}
              {error && <p className="text-xs text-red-600">{error}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => notesMutation.mutate({ id: finding.id, notes: draft })}
                  disabled={notesMutation.isPending || tooLong}
                  className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={() => { setEditing(false); setDraft(finding.notes ?? ''); setError(null); }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : finding.notes ? (
            <div className="space-y-2">
              <div
                className="text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(finding.notes) }}
              />
              <button
                onClick={() => { setDraft(finding.notes ?? ''); setEditing(true); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
              >
                <PencilIcon /> {t('myFindings.edit')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-400">{t('myFindings.notesEmpty')}</p>
              <button
                onClick={() => { setDraft(''); setEditing(true); }}
                className="px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400"
              >
                {t('myFindings.addNote')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MyFindings() {
  const { t } = useTranslation();
  // Finding 36: an ERROR must never render as "no requests" (the failed list
  // showed the empty state — users read it as "my requests vanished"). The
  // empty state is only for a successful fetch with zero rows.
  const { data: findings, isLoading, isError } = useQuery({
    queryKey: ['my-findings'],
    queryFn: () => findingsApi.list(),
    refetchInterval: 60_000,
  });

  // The open modal: which finding, and whether it opened straight into
  // notes-editing mode (the row's „Redaguoti"). Only the id is held — the
  // finding object re-resolves from the (possibly refetched) list, so a
  // saved note shows up in the open modal.
  const [modal, setModal] = useState<{ id: number; startEdit: boolean } | null>(null);

  const byAge = [...(findings ?? [])].sort(
    (a, b) => parseAsUtc(b.createdAtUtc).getTime() - parseAsUtc(a.createdAtUtc).getTime(),
  );
  const modalFinding = modal ? (findings ?? []).find(f => f.id === modal.id) : undefined;

  // NEW REQUEST: straight to the assistant, framed to listen (the copilot's §5
  // rules make it register what follows as product-bug/enhancement). One click
  // opens the chat pre-loaded — the user just describes what's wrong or wanted.
  const newRequest = () =>
    sendCopilotMessage(t('myFindings.newRequestPrompt'));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('myFindings.title')}</h1>
        <button
          onClick={newRequest}
          className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          + {t('myFindings.newRequest')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-4">{t('common.loading')}</p>
      ) : isError ? (
        <div className="py-8 text-center">
          <p className="text-sm text-red-600">{t('myFindings.loadError')}</p>
          <p className="mt-1 text-xs text-gray-400">{t('myFindings.loadErrorHint')}</p>
        </div>
      ) : byAge.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-gray-400">{t('myFindings.empty')}</p>
          {/* Finding 36: users believed their chat-filed requests had landed
              while they sat (or silently expired) on confirmation cards — say
              where requests come from so an empty list reads as "none filed",
              never as "filed and lost". */}
          <p className="mt-1 text-xs text-gray-400">{t('myFindings.emptyHint')}</p>
          <button
            onClick={newRequest}
            className="mt-4 px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400"
          >
            {t('myFindings.newRequest')} →
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2 font-semibold">{t('findings.col.title')}</th>
                <th className="px-4 py-2 font-semibold w-36">{t('findings.col.category')}</th>
                <th className="px-4 py-2 font-semibold w-32">{t('findings.col.status')}</th>
                <th className="px-4 py-2 font-semibold w-24">{t('findings.col.age')}</th>
                <th className="px-4 py-2 font-semibold w-44">{t('findings.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {byAge.map((f: CopilotFindingDto) => (
                <tr key={f.id} className="border-t border-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="text-gray-800">{f.title}</div>
                    {f.suggestedFix && (
                      <div className="text-xs text-gray-400 truncate max-w-xl">{f.suggestedFix}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${CATEGORY_CHIP[f.category] ?? 'bg-gray-100 text-gray-600'}`}>
                      {catLabel(f.category, t)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_CHIP[f.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {statusLabel(f.status, t)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">
                    {ageLabel(f.createdAtUtc, t)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setModal({ id: f.id, startEdit: false })}
                        aria-label={`${t('myFindings.view')}: ${f.title}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                      >
                        <EyeIcon /> {t('myFindings.view')}
                      </button>
                      {!!f.notes && (
                        <button
                          onClick={() => setModal({ id: f.id, startEdit: true })}
                          aria-label={`${t('myFindings.edit')}: ${f.title}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                        >
                          <PencilIcon /> {t('myFindings.edit')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && modalFinding && (
        <FindingModal
          key={modalFinding.id}
          finding={modalFinding}
          startInEdit={modal.startEdit}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
